import { db } from "@app/firebase"
import { doc, getDoc } from "firebase/firestore"
import { 
  FirestoreCollections, 
  Student,
  StudentTimeAccommodation,
  TestAdministrationSettings,
  TestAdministrationTimingOptions
} from "../types"

export const getStudentById = async (id: string): Promise<Student | null> => {
  try {
    const docRef = doc(db, FirestoreCollections.students, id)
    const docSnap = await getDoc(docRef)
    
    if (docSnap.exists()) {
      return docSnap.data() as Student
    }

    return null
  } catch (e) {
    console.error('Cannot get student data: ', e)
    return null
  }
}

export const convertStudentAccommodationToSettings = (
  accommodation?: StudentTimeAccommodation
): TestAdministrationSettings | null => {
  if (!accommodation) return null

  // Check each accommodation type (only one should be enabled)
  if (accommodation.unlimitedTime?.enabled) {
    return {
      timing: TestAdministrationTimingOptions.UNTIMED
    }
  }

  if (accommodation.doubleTime?.enabled) {
    return {
      timing: TestAdministrationTimingOptions.DOUBLE
    }
  }

  if (accommodation.timeAndHalf?.enabled) {
    return {
      timing: TestAdministrationTimingOptions.TIME_AND_A_HALF
    }
  }

  if (accommodation.customTime?.enabled && accommodation.customTime?.settings?.value) {
    return {
      timing: TestAdministrationTimingOptions.CUSTOM,
      customTimePercentage: accommodation.customTime.settings.value
    }
  }

  if (accommodation.noTimingAccomdations?.enabled) {
    return {
      timing: TestAdministrationTimingOptions.STANDARD
    }
  }

  return null
}

/**
 * Checks if a student has an effective accommodation (>100% time multiplier)
 * An effective accommodation is one that increases test time beyond the standard amount.
 * This is used to determine whether to protect the student's accommodation from being reduced.
 * 
 * @param accommodation The student's time accommodation settings
 * @returns true if the student has an accommodation that provides >100% time
 */
export const hasEffectiveAccommodation = (
  accommodation?: StudentTimeAccommodation
): boolean => {
  if (!accommodation) return false

  // Unlimited time is always an effective accommodation
  if (accommodation.unlimitedTime?.enabled) {
    return true
  }

  // Double time (200%) is an effective accommodation
  if (accommodation.doubleTime?.enabled) {
    return true
  }

  // Time and a half (150%) is an effective accommodation
  if (accommodation.timeAndHalf?.enabled) {
    return true
  }

  // Custom time is effective if it's greater than 100%
  if (accommodation.customTime?.enabled && accommodation.customTime?.settings?.value) {
    return accommodation.customTime.settings.value > 100
  }

  // No timing accommodations or standard time (100%) is not an effective accommodation
  return false
}